import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, VT_LABEL, VT_FILTER_TABS, SearchableSelect
} from '../components/SharedUI';


// ═══════════════════════════════════════════════════════════════
// INVOICES PAGE
// ═══════════════════════════════════════════════════════════════
const INV_STATUS = {
  unpaid: { label: 'غير مدفوع', color: '#E74C3C', bg: '#FEE2E220' },
  partial: { label: 'جزئي', color: '#E67E22', bg: '#FEF3C720' },
  paid: { label: 'مدفوع', color: '#17A589', bg: '#D1FAE520' },
  overdue: { label: 'متأخر', color: '#C0392B', bg: '#FDEDEC' },
  cancelled: { label: 'ملغي', color: '#95A5A6', bg: '#F8F9FA' },
};
const INV_TYPE = { project: 'مشروع', subscription: 'اشتراك', overage: 'تجاوز' };
const PAY_METHODS = [
  { v: 'cash', l: 'كاش' },
  { v: 'bank_transfer', l: 'تحويل بنكي' },
  { v: 'zain_cash', l: 'ZainCash' },
  { v: 'fib', l: 'FIB' },
  { v: 'card', l: 'بطاقة' },
];

const BLANK_CREATE = () => ({
  client_id: '', invoice_type: 'project', issue_date: today(),
  currency: 'IQD', total_iqd: '', total_usd: '', notes: '', discount_type: 'none', discount_value: ''
});

const QUICK_SERVICES = [
  { v: 'video', l: '🎬 فيديو', hasSeconds: true },
  { v: 'ai_influencer', l: '🤖 AI Influencer', hasSeconds: true },
  { v: 'ai_clone', l: '🧬 AI Clone', hasSeconds: true },
  { v: 'design', l: '🎨 تصميم', hasSeconds: false },
  { v: 'audio', l: '🎙️ صوت', hasSeconds: true },
  { v: 'custom', l: '✏️ خدمة أخرى', hasSeconds: true },
];
const BLANK_PAY = () => ({ amount_iqd: '', method: 'cash', pay_date: today(), receipt_ref: '', note: '' });

async function printInvoice(inv) {
  // Fetch full invoice detail (with lines) + system settings for terms
  let fullInv = inv;
  let termsText = '';
  try {
    const [detail, sysSettings] = await Promise.all([
      api.get(`/invoices/${inv.id}`).then(r => r.data).catch(() => inv),
      api.get('/settings/system').then(r => r.data).catch(() => [])
    ]);
    fullInv = detail;
    const settingsObj = {};
    (sysSettings || []).forEach(s => { settingsObj[s.key] = s.value; });
    termsText = settingsObj.invoice_terms || '';
  } catch { /* use original inv */ }

  const lines = fullInv.lines || [];

  // Build lines table HTML
  let linesHtml = '';
  if (lines.length > 0) {
    const lineRows = lines.map((l, i) => {
      const qty = parseFloat(l.quantity) || 1;
      const price = parseFloat(l.unit_price_iqd) || 0;
      const total = parseFloat(l.line_total_iqd) || (qty * price);
      return `<tr>
        <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;text-align:center;color:#64748B;font-size:13px">${i + 1}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;font-size:14px;font-weight:600">${l.description || '—'}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;text-align:center;font-size:14px">${qty}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;text-align:center;font-size:14px">${formatIQD(price)}</td>
        <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;text-align:center;font-size:14px;font-weight:700">${formatIQD(total)}</td>
      </tr>`;
    }).join('');

    linesHtml = `
    <div style="margin:28px 0 10px">
      <div style="font-size:14px;font-weight:800;color:#0F1E33;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #9B8EC4">📋 تفاصيل البنود</div>
      <table style="width:100%;border-collapse:collapse;background:#fff;border:1px solid #E2E8F0;border-radius:10px;overflow:hidden">
        <thead>
          <tr style="background:#F8FAFC">
            <th style="padding:10px 14px;text-align:center;font-size:12px;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0;width:40px">#</th>
            <th style="padding:10px 14px;text-align:right;font-size:12px;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0">الوصف</th>
            <th style="padding:10px 14px;text-align:center;font-size:12px;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0;width:60px">الكمية</th>
            <th style="padding:10px 14px;text-align:center;font-size:12px;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0;width:120px">سعر الوحدة</th>
            <th style="padding:10px 14px;text-align:center;font-size:12px;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0;width:120px">الإجمالي</th>
          </tr>
        </thead>
        <tbody>${lineRows}</tbody>
      </table>
    </div>`;
  }

  // Build terms HTML
  let termsHtml = '';
  if (termsText.trim()) {
    const termsLines = termsText.split('\n').map(line => `<div style="padding:2px 0">${line}</div>`).join('');
    termsHtml = `
    <div style="margin:24px 0 0;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:10px;padding:16px 20px">
      <div style="font-size:13px;font-weight:700;color:#0F1E33;margin-bottom:8px">📌 الشروط والأحكام</div>
      <div style="font-size:12px;color:#374151;line-height:1.8">${termsLines}</div>
    </div>`;
  }

  const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>فاتورة ${fullInv.invoice_number}</title>
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;600;700;800&display=swap" rel="stylesheet">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Tajawal', sans-serif; background: #fff; color: #0F1E33; padding: 40px; max-width: 800px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #9B8EC4; }
  .agency-name { font-size: 24px; font-weight: 900; letter-spacing: 3px; }
  .agency-name .ms { color: #1A1A2E; }
  .agency-name .media { color: #9B8EC4; }
  .agency-sub { font-size: 13px; color: #64748B; margin-top: 4px; }
  .inv-title { font-size: 28px; font-weight: 800; color: #0F1E33; text-align: left; }
  .inv-num { font-size: 14px; color: #64748B; text-align: left; margin-top: 4px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
  .info-box { background: #F8FAFC; border-radius: 10px; padding: 16px 20px; }
  .info-label { font-size: 11px; color: #94A3B8; font-weight: 600; text-transform: uppercase; margin-bottom: 6px; }
  .info-value { font-size: 15px; font-weight: 700; color: #0F1E33; }
  .amounts { margin: 30px 0; }
  .amount-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #F1F5F9; font-size: 15px; }
  .amount-row.total { font-size: 18px; font-weight: 800; border-bottom: none; border-top: 2px solid #E2E8F0; padding-top: 16px; margin-top: 4px; }
  .green { color: #17A589; }
  .red { color: #E74C3C; }
  .blue { color: #9B8EC4; }
  .status-badge { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 13px; font-weight: 700; margin-top: 16px; }
  .status-paid { background: #D1FAE5; color: #065F46; }
  .status-partial { background: #FEF3C7; color: #92400E; }
  .status-unpaid { background: #FEE2E2; color: #991B1B; }
  .notes-box { background: #F8FAFC; border-radius: 10px; padding: 16px; margin: 20px 0; font-size: 14px; color: #374151; line-height: 1.7; }
  .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #E2E8F0; color: #94A3B8; font-size: 13px; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="agency-name"><span class="ms">MS </span><span class="media">MEDIA</span></div>
    <div class="agency-sub">نظام المحاسبة والإدارة</div>
  </div>
  <div>
    <div class="inv-title">فاتورة</div>
    <div class="inv-num">${fullInv.invoice_number}</div>
  </div>
</div>
<div class="info-grid">
  <div class="info-box">
    <div class="info-label">العميل</div>
    <div class="info-value">${fullInv.client_name || '—'}</div>
  </div>
  <div class="info-box">
    <div class="info-label">نوع الفاتورة</div>
    <div class="info-value">${fullInv.invoice_type === 'subscription' ? 'اشتراك شهري' : fullInv.invoice_type === 'overage' ? 'تجاوز' : 'مشروع / فيديو'}</div>
  </div>
  <div class="info-box" style="grid-column: span 2">
    <div class="info-label">تاريخ الإصدار</div>
    <div class="info-value">${fullInv.issue_date || '—'}</div>
  </div>
</div>
${fullInv.notes ? '<div class="notes-box">📝 ' + fullInv.notes + '</div>' : ''}
${linesHtml}
<div class="amounts">
${fullInv.discount_type && fullInv.discount_type !== 'none' ? `
  <div class="amount-row" style="color: #64748B;">
    <span>المبلغ الإجمالي (قبل الخصم)</span>
    <span><strong>${formatIQD(fullInv.subtotal_iqd || fullInv.total_iqd)}</strong></span>
  </div>
  <div class="amount-row" style="color: #E74C3C;">
    <span>قيمة الخصم (${fullInv.discount_type === 'percentage' ? fullInv.discount_value + '%' : formatIQD(fullInv.discount_value)})</span>
    <span><strong>- ${formatIQD((fullInv.subtotal_iqd || fullInv.total_iqd) - fullInv.total_iqd)}</strong></span>
  </div>
` : ''}
  <div class="amount-row">
    <span>${fullInv.discount_type && fullInv.discount_type !== 'none' ? 'الإجمالي النهائي' : 'الإجمالي'}</span>
    <span class="blue"><strong>${formatIQD(fullInv.total_iqd || 0)}</strong> &nbsp;|&nbsp; ${formatUSD(fullInv.total_usd || 0)}</span>
  </div>
  <div class="amount-row">
    <span>المدفوع</span>
    <span class="green"><strong>${formatIQD(fullInv.paid_iqd || 0)}</strong> &nbsp;|&nbsp; ${formatUSD(fullInv.paid_usd || 0)}</span>
  </div>
  <div class="amount-row total">
    <span>المتبقي</span>
    <span class="${((fullInv.total_iqd || 0) - (fullInv.paid_iqd || 0)) > 0 ? 'red' : 'green'}"><strong>${formatIQD(Math.max((fullInv.total_iqd || 0) - (fullInv.paid_iqd || 0), 0))}</strong></span>
  </div>
</div>
<div>
  <span class="status-badge ${fullInv.payment_status === 'paid' ? 'status-paid' : fullInv.payment_status === 'partial' ? 'status-partial' : 'status-unpaid'}">
    ${fullInv.payment_status === 'paid' ? '✅ مدفوع' : fullInv.payment_status === 'partial' ? '⏳ جزئي' : '❌ غير مدفوع'}
  </span>
</div>
${termsHtml}
<div class="footer">
  شكراً لتعاملكم معنا — MS MEDIA<br>
  <small>تم إصدار هذه الفاتورة إلكترونياً</small>
</div>
</body>
</html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 800);
}


// ─── Quick Invoice helper ────────────────────────────────────────────────────
function extractQuickName(notes) {
  if (!notes) return null;
  const match = notes.match(/^\[([^\]]+)\]/);
  return match ? match[1].trim() : null;
}

// ─── Client Profile Modal ────────────────────────────────────────────────────
export default function InvoicesPage() {
  const { user } = useAuth();
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const BLANK_QUICK_ITEM = () => ({ service: 'video', calculationMode: 'seconds', seconds: 30, customPrice: '', description: '', pricePerSecond: 1500 });
  const [quickForm, setQuickForm] = useState({
    client_id: '', newClientName: '', guestName: '', clientMode: 'select', note: '', items: [BLANK_QUICK_ITEM()],
    referring_employee_id: '', commission_rate: '', discount_type: 'none', discount_value: ''
  });
  const [payModal, setPayModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [createForm, setCreateForm] = useState(BLANK_CREATE);
  const [payForm, setPayForm] = useState(BLANK_PAY);
  const [invSearch, setInvSearch] = useState('');
  const navigate = useNavigate();
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [createItems, setCreateItems] = useState([]);

  // Helper: fetch full invoice detail then open edit modal
  const openEditModal = async (inv) => {
    setEditLoading(true);
    try {
      const full = await getInvoice(inv.id);
      // Convert existing lines to editable items
      const existingLines = (full.lines || []).map(l => ({
        id: l.id,
        service: l.service_type || 'custom',
        description: l.description || '',
        seconds: l.unit === 'second' ? (l.quantity || 0) : '',
        customPrice: l.unit !== 'second' ? (l.line_total_iqd || l.unit_price_iqd || 0) : '',
        pricePerSecond: l.unit === 'second' ? (l.unit_price_iqd || 1500) : 1500,
        quantity: l.quantity || 1,
        unit: l.unit || 'unit',
        unit_price_iqd: l.unit_price_iqd || 0,
        unit_price_usd: l.unit_price_usd || 0,
      }));
      setEditForm({
        client_id: String(full.client_id || ''),
        invoice_type: full.invoice_type || 'project',
        issue_date: full.issue_date || '',
        due_date: full.due_date || '',
        notes: full.notes || '',
        discount_type: full.discount_type || 'none',
        discount_value: full.discount_value || '',
        items: existingLines.length > 0 ? existingLines : [{
          service: 'custom', seconds: '', description: full.notes || 'المبلغ الإجمالي',
          customPrice: String(full.total_iqd || 0), pricePerSecond: 1500
        }],
        referring_employee_id: full.referring_employee_id || '',
        commission_rate: full.commission_rate || ''
      });
      setEditModal(full);
    } catch (err) {
      toast.error('خطأ في تحميل تفاصيل الفاتورة');
    }
    setEditLoading(false);
  };

  const load = useCallback(() => {
    const p = {};
    if (statusFilter) p.status = statusFilter;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    getInvoices(p).then(setInvoices);
  }, [statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getClients().then(setClients);
    getEmployees().then(setEmployees);
  }, []);

  const handleEmployeeSelect = (empId, setForm) => {
    const emp = employees.find(e => e.id === Number(empId));
    setForm(f => ({ ...f, referring_employee_id: empId, commission_rate: emp ? (emp.commission_rate || 0) : '' }));
  };

  // ── Derived totals ──────────────────────────────────────────
  const totalAll = invoices.reduce((s, i) => s + (i.total_iqd || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (i.paid_iqd || 0), 0);
  const totalOutstanding = invoices
    .filter(i => i.payment_status !== 'paid')
    .reduce((s, i) => s + Math.max((i.total_iqd || 0) - (i.paid_iqd || 0), 0), 0);
  const countOverdue = invoices.filter(i =>
    i.due_date && i.due_date < today() && i.payment_status !== 'paid'
  ).length;

  // ── Handlers ────────────────────────────────────────────────
  const handleCreate = async e => {
    e.preventDefault();
    if (!createForm.client_id) return toast.error('اختر العميل');
    try {
      const data = { ...createForm };

      // If we have items (Calculation Basics), build the lines array
      if (createItems.length > 0) {
        data.lines = createItems.map(item => {
          const svc = QUICK_SERVICES.find(s => s.v === item.service);
          const hasSeconds = svc?.hasSeconds;
          const pps = parseInt(item.pricePerSecond) || 1500;
          const priceIqd = hasSeconds ? (parseInt(item.seconds) || 0) * pps : (parseInt(item.customPrice) || 0);
          return {
            description: item.description || (svc?.l || 'خدمة'),
            service_type: item.service,
            quantity: hasSeconds ? (parseInt(item.seconds) || 1) : 1,
            unit: hasSeconds ? 'second' : 'unit',
            unit_price_iqd: hasSeconds ? pps : priceIqd,
            unit_price_usd: (hasSeconds ? pps : priceIqd) / EXCHANGE_RATE
          };
        });
        data.total_iqd = createGrandTotalIqd;
        data.total_usd = createGrandTotalUsd;
      } else {
        if (data.currency === 'IQD') data.total_usd = (parseFloat(data.total_iqd || 0) / EXCHANGE_RATE).toFixed(2);
        else data.total_iqd = Math.round(parseFloat(data.total_usd || 0) * EXCHANGE_RATE);
      }

      await createInvoice(data);
      toast.success('تم إنشاء الفاتورة');
      setShowCreate(false);
      setCreateForm(BLANK_CREATE);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ في إنشاء الفاتورة'); }
  };

  const handlePay = async e => {
    e.preventDefault();
    const amt = parseFloat(payForm.amount_iqd);
    if (!amt || amt <= 0) return toast.error('أدخل مبلغاً صحيحاً');
    const remaining = (payModal.total_iqd || 0) - (payModal.paid_iqd || 0);
    if (amt > remaining + 1) return toast.error(`الحد الأقصى: ${formatIQD(remaining)}`);
    try {
      await recordPayment(payModal.id, { ...payForm, amount_usd: amt / EXCHANGE_RATE });
      toast.success('تم تسجيل الدفعة');
      setPayModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
  };

  const openPayModal = inv => {
    const remaining = (inv.total_iqd || 0) - (inv.paid_iqd || 0);
    setPayForm({ ...BLANK_PAY(), amount_iqd: remaining });
    setPayModal(inv);
  };

  // ── Live USD for create form ─────────────────────────────────
  const createPreviewUSD = createForm.currency === 'IQD'
    ? iqd2usd(parseFloat(createForm.total_iqd || 0))
    : parseFloat(createForm.total_usd || 0);

  // ── Live USD for pay form ────────────────────────────────────
  const payPreviewUSD = iqd2usd(parseFloat(payForm.amount_iqd || 0));

  const isOverdue = inv =>
    inv.due_date && inv.due_date < today() && inv.payment_status !== 'paid';

  // ── Client-side search filter ────────────────────────────────
  const displayInvoices = invSearch.trim()
    ? invoices.filter(inv => {
      const q = invSearch.trim().toLowerCase();
      const qn = extractQuickName(inv.notes) || '';
      return (
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.client_name || '').toLowerCase().includes(q) ||
        qn.toLowerCase().includes(q) ||
        (inv.notes || '').toLowerCase().includes(q)
      );
    })
    : invoices;

  // ── Render ───────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={20} color="#17A589" /> الفواتير
          <span className="dark:!text-gray-400" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted, #94A3B8)' }}>({invoices.length})</span>
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowQuick(true)} style={btn('#F59E0B')}>
            <Plus size={15} /> فاتورة سريعة ⚡
          </button>
          <button onClick={() => setShowCreate(true)} style={btn('#17A589')}>
            <Plus size={15} /> فاتورة جديدة
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="إجمالي الفواتير" iqd={totalAll} color="var(--text-dark-h, #0F1E33)" icon={<FileText />} />
        <StatCard label="المحصّل" iqd={totalPaid} color="#17A589" icon={<CheckCircle />} />
        <StatCard label="المتبقي" iqd={totalOutstanding} color={totalOutstanding > 0 ? '#E74C3C' : '#17A589'} icon={<DollarSign />} />
        <StatCard label="متأخرة السداد" rawValue={countOverdue} color={countOverdue > 0 ? '#E74C3C' : '#17A589'}
          sub={countOverdue > 0 ? 'تحتاج متابعة' : 'لا توجد متأخرات'} icon={<AlertCircle />} />
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['', 'الكل'], ['unpaid', 'غير مدفوع'], ['partial', 'جزئي'], ['paid', 'مدفوع']].map(([v, l]) => (
          <FilterBtn key={v} active={statusFilter === v} color="#17A589" onClick={() => setStatusFilter(v)}>{l}</FilterBtn>
        ))}
        <input
          value={invSearch} onChange={e => setInvSearch(e.target.value)}
          placeholder="🔍 بحث..."
          style={{ padding: '6px 12px', borderRadius: 20, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 13, fontFamily: 'Tajawal, sans-serif', outline: 'none', minWidth: 160 }}
        />
        <div style={{ marginRight: 'auto' }}>
          <DateRangeFilter from={fromDate} to={toDate} onFrom={setFromDate} onTo={setToDate} onClear={() => { setFromDate(''); setToDate(''); }} />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                {['رقم الفاتورة', 'العميل', 'النوع', 'الاستحقاق', 'الإجمالي', 'المدفوع', 'المتبقي', 'الحالة', ''].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayInvoices.map((inv, i) => {
                const st = INV_STATUS[inv.payment_status] || INV_STATUS.unpaid;
                const over = isOverdue(inv);
                const remain = (inv.total_iqd || 0) - (inv.paid_iqd || 0);
                const qn = extractQuickName(inv.notes);
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                    <td style={td({ fontWeight: 700, color: '#9B8EC4', cursor: 'pointer' })}
                      onClick={() => getInvoice(inv.id).then(setDetailModal).catch(() => setDetailModal(inv))}>
                      <div>{inv.invoice_number}</div>
                      <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', fontWeight: 400 }}>{inv.issue_date}</div>
                    </td>
                    <td style={td()}>
                      {qn ? (
                        <div>
                          <span style={{ fontWeight: 700, color: '#F59E0B' }}>⚡ {qn}</span>
                          <span className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', display: 'block' }}>فاتورة سريعة</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button onClick={(e) => { e.stopPropagation(); navigate('/clients/' + inv.client_id); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', color: '#4F46E5', fontWeight: 700, fontSize: 14 }}>
                            {inv.client_name || '—'}
                          </button>
                          {(() => {
                            const p = inv.client_phone || clients.find(c => c.id === inv.client_id)?.phone;
                            if (!p) return null;
                            const trackToken = inv.client_tracking_token || clients.find(c => c.id === inv.client_id)?.tracking_token;
                            const trackAppend = trackToken ? `\n\n🔗 لمتابعة التفاصيل والفواتير عبر الرابط الخاص بك:\n${window.location.origin}/track/${trackToken}` : '';
                            const textMsg = encodeURIComponent(`مرحباً ${inv.client_name}،\nإليك تفاصيل الفاتورة الخاصة بك:\n\n📄 رقم الفاتورة: ${inv.invoice_number}\n💰 الإجمالي: ${formatIQD(inv.total_iqd)}\n✅ المدفوع: ${formatIQD(inv.paid_iqd)}\n⏳ المتبقي: ${formatIQD(remain)}\n\nتاريخ الاستحقاق: ${inv.due_date || '—'}${trackAppend}\n\nشكراً لتعاملك معنا — MS MEDIA`);
                            return (
                              <a href={'https://wa.me/' + p.replace(/\\D/g, '') + '?text=' + textMsg} target="_blank" rel="noreferrer"
                                style={{ color: '#4ADE80', textDecoration: 'none', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}
                                onClick={e => e.stopPropagation()}>
                                📱 {p}
                              </a>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                    <td style={td({ color: 'var(--text-gray, #64748B)' })}>
                      <span style={pill('#F1F5F9', 'var(--text-gray, #64748B)')}>{INV_TYPE[inv.invoice_type] || inv.invoice_type}</span>
                    </td>
                    <td style={td({ color: over ? '#E74C3C' : 'var(--text-gray, #64748B)', fontWeight: over ? 700 : 400 })}>
                      {inv.due_date
                        ? <>{over && '⚠ '}{inv.due_date}</>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={td()}><Dual iqd={inv.total_iqd} usd={inv.total_usd} bold /></td>
                    <td style={td()}><Dual iqd={inv.paid_iqd} usd={inv.paid_usd} /></td>
                    <td style={td()}><Dual iqd={remain} redIqd={remain > 0} bold={remain > 0} /></td>
                    <td style={td()}>
                      <span style={pill(st.bg, st.color)}>{st.label}</span>
                    </td>
                    <td style={td()}>
                      {inv.payment_status !== 'paid' && (
                        <button onClick={() => openPayModal(inv)}
                          style={{ ...btn('#17A589'), padding: '5px 11px', fontSize: 12 }}>
                          <CreditCard size={13} /> دفعة
                        </button>
                      )}
                      <button onClick={() => printInvoice(inv)} title="طباعة الفاتورة"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-gray, #64748B)', padding: '4px 6px', borderRadius: 6, display: 'inline-flex', marginRight: 4 }}>
                        🖨️
                      </button>
                      {inv.payment_status !== 'cancelled' && (isOwnerOrAdmin || inv.payment_status !== 'paid') && (
                        <button onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(inv);
                        }}
                          title="تعديل الفاتورة" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5', padding: '4px 6px', borderRadius: 6, display: 'inline-flex', marginRight: 4 }}>
                          ✏️
                        </button>
                      )}
                      {isOwnerOrAdmin && (
                        <button onClick={async e => {
                          e.stopPropagation();
                          if (!window.confirm(`حذف الفاتورة ${inv.invoice_number} نهائياً؟`)) return;
                          try { await deleteInvoice(inv.id); toast.success('تم الحذف'); load(); }
                          catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
                        }}
                          title="حذف نهائي" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: '4px 6px', borderRadius: 6, display: 'inline-flex', marginRight: 4 }}>
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {invoices.length === 0 && <EmptyRow cols={9} msg="لا توجد فواتير" />}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════ QUICK INVOICE MODAL (MULTI-ITEM) ═══════════════ */}
      {showQuick && (() => {
        // Compute per-item prices
        const itemPrices = quickForm.items.map(item => {
          const svc = QUICK_SERVICES.find(s => s.v === item.service) || QUICK_SERVICES[0];
          const hasSeconds = item.calculationMode ? (item.calculationMode === 'seconds') : svc.hasSeconds;
          const pps = parseInt(item.pricePerSecond) || 1500;
          const priceIqd = hasSeconds
            ? (parseInt(item.seconds) || 0) * pps
            : (parseInt(item.customPrice) || 0);
          const priceUsd = Math.round(priceIqd / EXCHANGE_RATE * 100) / 100;
          return { priceIqd, priceUsd, svc, hasSeconds, pps };
        });
        const subTotalIqd = itemPrices.reduce((s, p) => s + p.priceIqd, 0);
        const subTotalUsd = itemPrices.reduce((s, p) => s + p.priceUsd, 0);
        const qDVal = parseFloat(quickForm.discount_value) || 0;
        let grandTotalIqd = subTotalIqd;
        if (quickForm.discount_type === 'amount') grandTotalIqd -= qDVal;
        if (quickForm.discount_type === 'percentage') grandTotalIqd -= (subTotalIqd * (qDVal / 100));
        grandTotalIqd = Math.max(0, grandTotalIqd);
        const grandTotalUsd = Math.round((grandTotalIqd / EXCHANGE_RATE) * 100) / 100;

        const updateItem = (idx, patch) => {
          setQuickForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
        };
        const removeItem = (idx) => {
          setQuickForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
        };
        const addItem = () => {
          setQuickForm(f => ({ ...f, items: [...f.items, BLANK_QUICK_ITEM()] }));
        };

        return (
          <Modal onClose={() => setShowQuick(false)} title="⚡ فاتورة سريعة" width={560}>
            <form onSubmit={async e => {
              e.preventDefault();
              // Validate
              if (quickForm.items.length === 0) return toast.error('أضف خدمة واحدة على الأقل');
              if (grandTotalIqd <= 0) return toast.error('أدخل الأسعار / المدد');

              // Resolve client
              let clientId = quickForm.client_id;
              let finalNoteAppend = '';
              if (quickForm.clientMode === 'guest') {
                let guestClient = clients.find(c => c.name === 'عميل عابر');
                if (!guestClient) {
                  try {
                    guestClient = await createClient({ name: 'عميل عابر', client_type: 'individual' });
                    load(); // refresh silently
                  } catch (err) { return toast.error(err.response?.data?.error || 'خطأ بإنشاء العميل العابر'); }
                }
                clientId = guestClient.id;
                if (quickForm.guestName.trim()) finalNoteAppend = ' | العميل العابر: ' + quickForm.guestName.trim();
              } else if (quickForm.clientMode === 'new') {
                const newName = quickForm.newClientName.trim();
                if (!newName) return toast.error('أدخل اسم العميل الجديد');
                try {
                  const c = await createClient({ name: newName, client_type: 'individual' });
                  clientId = c.id;
                  // refresh clients list silently
                  load();
                } catch (err) { return toast.error(err.response?.data?.error || 'خطأ بإنشاء العميل'); }
              } else {
                clientId = clientId || (clients[0]?.id);
                if (!clientId) return toast.error('لا يوجد عملاء — اختر "كتابة اسم جديد"');
              }

              // Build lines for backend
              const lines = quickForm.items.map((item, i) => {
                const p = itemPrices[i];
                const p_iqd = p.hasSeconds ? p.pps : p.priceIqd;
                return {
                  description: item.description || p.svc.l,
                  service_type: item.service,
                  quantity: p.hasSeconds ? (parseInt(item.seconds) || 1) : 1,
                  unit: p.hasSeconds ? 'second' : 'unit',
                  unit_price_iqd: p_iqd,
                  unit_price_usd: Math.round(p_iqd / EXCHANGE_RATE * 100) / 100,
                };
              });

              // Build notes summary
              const itemsSummary = quickForm.items.map((item, i) => {
                const p = itemPrices[i];
                const label = p.svc.l.replace(/^[^\s]+\s/, '');
                return p.hasSeconds ? `${label} ${item.seconds}ث` : `${label} ${formatIQD(p.priceIqd)}`;
              }).join(' + ');
              const noteText = `${itemsSummary}${quickForm.note ? ' — ' + quickForm.note : ''}${finalNoteAppend}`;

              try {
                await createInvoice({
                  client_id: clientId,
                  invoice_type: 'project',
                  issue_date: today(),
                  currency: 'IQD',
                  subtotal_iqd: subTotalIqd,
                  subtotal_usd: subTotalUsd,
                  discount_type: quickForm.discount_type,
                  discount_value: quickForm.discount_value,
                  total_iqd: grandTotalIqd,
                  total_usd: grandTotalUsd,
                  notes: noteText,
                  lines,
                  referring_employee_id: quickForm.referring_employee_id,
                  commission_rate: quickForm.commission_rate
                });
                toast.success(`✅ فاتورة سريعة — ${quickForm.items.length} خدمة — ${formatIQD(grandTotalIqd)}`);
                setShowQuick(false);
                setQuickForm({
                  client_id: '', newClientName: '', guestName: '', clientMode: 'select', note: '',
                  items: [BLANK_QUICK_ITEM()], referring_employee_id: '', commission_rate: '',
                  discount_type: 'none', discount_value: ''
                });
                load();
              } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* ── Client selection mode ── */}
                <LabeledInput label="العميل">
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setQuickForm(f => ({ ...f, clientMode: 'select' }))}
                      style={{
                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                        border: `2px solid ${quickForm.clientMode === 'select' ? '#9B8EC4' : 'var(--border-light, #E2E8F0)'}`,
                        background: quickForm.clientMode === 'select' ? '#F3F0FF' : 'var(--bg-flat-white, white)',
                        color: quickForm.clientMode === 'select' ? '#5B21B6' : 'var(--text-gray, #64748B)',
                        fontWeight: 700, fontSize: 12
                      }}>👤 اختيار من القائمة</button>
                    <button type="button" onClick={() => setQuickForm(f => ({ ...f, clientMode: 'new' }))}
                      style={{
                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                        border: `2px solid ${quickForm.clientMode === 'new' ? '#9B8EC4' : 'var(--border-light, #E2E8F0)'}`,
                        background: quickForm.clientMode === 'new' ? '#F3F0FF' : 'var(--bg-flat-white, white)',
                        color: quickForm.clientMode === 'new' ? '#5B21B6' : 'var(--text-gray, #64748B)',
                        fontWeight: 700, fontSize: 12
                      }}>✏️ كتابة اسم جديد</button>
                    <button type="button" onClick={() => setQuickForm(f => ({ ...f, clientMode: 'guest' }))}
                      style={{
                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                        border: `2px solid ${quickForm.clientMode === 'guest' ? '#17A589' : 'var(--border-light, #E2E8F0)'}`,
                        background: quickForm.clientMode === 'guest' ? '#E6F4F1' : 'var(--bg-flat-white, white)',
                        color: quickForm.clientMode === 'guest' ? '#065F46' : 'var(--text-gray, #64748B)',
                        fontWeight: 700, fontSize: 12
                      }}>🚶‍♂️ عميل عابر (بدون تسجيل)</button>
                  </div>
                  {quickForm.clientMode === 'select' ? (
                    <SearchableSelect
                      value={quickForm.client_id}
                      onChange={val => setQuickForm(f => ({ ...f, client_id: val }))}
                      options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                      placeholder="— اختر عميلاً —"
                      style={S.input}
                    />
                  ) : quickForm.clientMode === 'new' ? (
                    <input value={quickForm.newClientName}
                      onChange={e => setQuickForm(f => ({ ...f, newClientName: e.target.value }))}
                      style={S.input} placeholder="اكتب اسم العميل الجديد..." autoFocus />
                  ) : (
                    <input value={quickForm.guestName}
                      onChange={e => setQuickForm(f => ({ ...f, guestName: e.target.value }))}
                      style={S.input} placeholder="اسم العميل لمعرفته بالتفاصيل (اختياري)..." autoFocus />
                  )}
                </LabeledInput>

                {/* ── LINE ITEMS ── */}
                <div className="dark:!text-gray-400" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-gray, #64748B)', marginBottom: -6 }}>الخدمات / البنود</div>
                {quickForm.items.map((item, idx) => {
                  const p = itemPrices[idx];
                  const hasSeconds = p.hasSeconds;
                  return (
                    <div key={idx} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{
                      background: '#FAFBFC', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 12,
                      padding: '12px 14px', position: 'relative'
                    }}>
                      {/* Remove item button */}
                      {quickForm.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(idx)}
                          style={{
                            position: 'absolute', top: 8, left: 8, background: '#FEE2E2', border: 'none',
                            borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#DC2626'
                          }}>×</button>
                      )}
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9B8EC4', marginBottom: 8 }}>بند {idx + 1}</div>

                      {/* Service type */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {QUICK_SERVICES.map(s => (
                          <button key={s.v} type="button"
                            onClick={() => updateItem(idx, { service: s.v })}
                            style={{
                              padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                              border: `2px solid ${item.service === s.v ? '#F59E0B' : 'var(--border-light, #E2E8F0)'}`,
                              background: item.service === s.v ? '#FEF3C7' : 'var(--bg-flat-white, white)',
                              color: item.service === s.v ? '#92400E' : 'var(--text-gray, #64748B)',
                              fontWeight: 700, fontSize: 12, fontFamily: 'Tajawal, sans-serif'
                            }}>{s.l}</button>
                        ))}
                      </div>

                      {/* Description (optional) */}
                      <input value={item.description}
                        onChange={e => updateItem(idx, { description: e.target.value })}
                        style={{ ...S.input, marginBottom: 8, fontSize: 12 }}
                        placeholder={`وصف اختياري (مثال: فيديو إعلان العطر)...`} />

                      {/* طريقة احتساب السعر للبند */}
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        <button type="button"
                          onClick={() => updateItem(idx, { calculationMode: 'seconds' })}
                          style={{
                            flex: 1, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                            border: `2px solid ${(!item.calculationMode || item.calculationMode === 'seconds') ? '#8B5CF6' : 'var(--border-light, #E2E8F0)'}`,
                            background: (!item.calculationMode || item.calculationMode === 'seconds') ? '#F5F3FF' : 'var(--bg-flat-white, white)',
                            color: (!item.calculationMode || item.calculationMode === 'seconds') ? '#6D28D9' : 'var(--text-gray, #64748B)',
                            fontWeight: 700, fontSize: 11, outline: 'none'
                          }}>
                          ⏱️ احتساب بالثواني
                        </button>
                        <button type="button"
                          onClick={() => updateItem(idx, { calculationMode: 'flat' })}
                          style={{
                            flex: 1, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                            border: `2px solid ${item.calculationMode === 'flat' ? '#10B981' : 'var(--border-light, #E2E8F0)'}`,
                            background: item.calculationMode === 'flat' ? '#ECFDF5' : 'var(--bg-flat-white, white)',
                            color: item.calculationMode === 'flat' ? '#047857' : 'var(--text-gray, #64748B)',
                            fontWeight: 700, fontSize: 11, outline: 'none'
                          }}>
                          💵 سعر ثابت مخصص
                        </button>
                      </div>

                      {/* Seconds or price */}
                      {hasSeconds ? (
                        <div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                            {[15, 30, 45, 60, 90, 120].map(s => (
                              <button key={s} type="button"
                                onClick={() => updateItem(idx, { seconds: s })}
                                style={{
                                  padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                                  border: `2px solid ${item.seconds === s ? '#F59E0B' : 'var(--border-light, #E2E8F0)'}`,
                                  background: item.seconds === s ? '#FEF3C7' : 'var(--bg-flat-white, white)',
                                  color: item.seconds === s ? '#92400E' : 'var(--text-gray, #64748B)',
                                  fontWeight: 700, fontSize: 12, fontFamily: 'Tajawal, sans-serif'
                                }}>{s}ث</button>
                            ))}
                          </div>
                          <input type="text" inputMode="decimal" value={item.seconds}
                            onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                            onChange={e => updateItem(idx, { seconds: parseInt(e.target.value) || '' })}
                            style={{ ...S.input, fontSize: 12, marginBottom: 8 }} placeholder="ثواني مخصصة..." />
                          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-dark-s, #374151)', background: 'var(--bg-flat-white, #FFFFFF)', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', marginTop: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                              <Clock size={14} color="#9B8EC4" />
                              <span style={{ fontWeight: 600 }}>سعر الثانية:</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input type="text" inputMode="decimal" value={item.pricePerSecond}
                                onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                                onChange={e => updateItem(idx, { pricePerSecond: parseInt(e.target.value) || '' })}
                                style={{ ...S.input, fontSize: 13, width: 90, padding: '6px 10px', textAlign: 'center', borderColor: '#9B8EC4' }} placeholder="1500" />
                              <span className="dark:!text-gray-400" style={{ fontWeight: 700, color: 'var(--text-gray, #64748B)', fontSize: 12 }}>د.ع / ث</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <input type="text" inputMode="decimal" value={item.customPrice}
                          onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                          onChange={e => updateItem(idx, { customPrice: e.target.value })}
                          style={{ ...S.input, fontSize: 12 }}
                          placeholder="السعر بالدينار (مثال: 150000)" />
                      )}

                      {/* Item price */}
                      {p.priceIqd > 0 && (
                        <div style={{ textAlign: 'left', marginTop: 6, fontSize: 12, fontWeight: 700, color: '#17A589' }}>
                          {formatIQD(p.priceIqd)} <span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontWeight: 400 }}>≈ {formatUSD(p.priceUsd)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add item button */}
                <button type="button" onClick={addItem}
                  className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
                    padding: '8px 16px', borderRadius: 10, cursor: 'pointer', border: '2px dashed #CBD5E1',
                    background: 'var(--bg-flat-white, white)', color: 'var(--text-gray, #64748B)', fontWeight: 700, fontSize: 13,
                    fontFamily: 'Tajawal, sans-serif', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6
                  }}>
                  <Plus size={15} /> إضافة خدمة أخرى
                </button>

                {/* ── Discount controls ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="الخصم">
                    <select value={quickForm.discount_type} onChange={e => setQuickForm(f => ({ ...f, discount_type: e.target.value }))} style={S.input}>
                      <option value="none">بدون خصم</option>
                      <option value="amount">مبلغ ثابت</option>
                      <option value="percentage">نسبة مئوية (%)</option>
                    </select>
                  </LabeledInput>
                  {quickForm.discount_type !== 'none' && (
                    <LabeledInput label={quickForm.discount_type === 'percentage' ? 'نسبة الخصم %' : 'مبلغ الخصم'}>
                      <input type="text" inputMode="decimal" value={quickForm.discount_value}
                        onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                        onChange={e => setQuickForm(f => ({ ...f, discount_value: e.target.value }))}
                        style={S.input} placeholder={quickForm.discount_type === 'percentage' ? 'مثال: 10' : 'مثال: 50000'} />
                    </LabeledInput>
                  )}
                </div>

                {/* ── Grand total preview ── */}
                {subTotalIqd > 0 && (
                  <div style={{
                    background: '#F0FDF4', border: '1px solid #BBF7D0',
                    borderRadius: 10, padding: '14px 16px', textAlign: 'center'
                  }}>
                    {quickForm.discount_type !== 'none' && grandTotalIqd < subTotalIqd && (
                      <div style={{ fontSize: 13, color: '#E74C3C', marginBottom: 6, fontWeight: 700 }}>
                        قبل الخصم: {formatIQD(subTotalIqd)} (خصم: {quickForm.discount_type === 'percentage' ? '%' + quickForm.discount_value : formatIQD(qDVal)})
                      </div>
                    )}
                    <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4 }}>
                      الإجمالي ({quickForm.items.length} {quickForm.items.length === 1 ? 'خدمة' : 'خدمات'})
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#17A589' }}>
                      {formatIQD(grandTotalIqd)}
                    </div>
                    <div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-muted, #94A3B8)', marginTop: 2 }}>
                      ≈ ${grandTotalUsd.toFixed(2)}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="بواسطة (إحالة موظف)">
                    <select value={quickForm.referring_employee_id}
                      onChange={e => handleEmployeeSelect(e.target.value, setQuickForm)}
                      style={S.input}>
                      <option value="">— لا يوجد —</option>
                      {employees.filter(e => e.active || e.id === Number(quickForm.referring_employee_id)).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </LabeledInput>
                  <LabeledInput label="نسبة العمولة (%)">
                    <input type="text" inputMode="decimal" value={quickForm.commission_rate}
                      onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                      onChange={e => setQuickForm(f => ({ ...f, commission_rate: e.target.value }))}
                      style={S.input} placeholder="مثال: 10" />
                    {parseFloat(quickForm.commission_rate || 0) > 0 && (
                      <div style={{ fontSize: 11, color: '#17A589', marginTop: 4, fontWeight: 700 }}>قيمة العمولة المحسوبة: {formatIQD(grandTotalIqd * (parseFloat(quickForm.commission_rate) / 100))}</div>
                    )}
                  </LabeledInput>
                </div>

                <LabeledInput label="ملاحظة إضافية">
                  <input value={quickForm.note}
                    onChange={e => setQuickForm(f => ({ ...f, note: e.target.value }))}
                    style={S.input} placeholder="اختياري..." />
                </LabeledInput>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" style={btn('#F59E0B', 'white')}>⚡ إنشاء الفاتورة ({quickForm.items.length} بند)</button>
                  <button type="button" onClick={() => setShowQuick(false)} style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}>إلغاء</button>
                </div>
              </div>
            </form>
          </Modal>
        );
      })()}

      {/* ═══════════════ CREATE INVOICE MODAL ═══════════════ */}
      {showCreate && (() => {
        // Calculation logic for create items
        const itemPrices = createItems.map(item => {
          const svc = QUICK_SERVICES.find(s => s.v === item.service) || QUICK_SERVICES[0];
          const hasSeconds = item.calculationMode ? (item.calculationMode === 'seconds') : svc.hasSeconds;
          const pps = parseInt(item.pricePerSecond) || 1500;
          const priceIqd = hasSeconds
            ? (parseInt(item.seconds) || 0) * pps
            : (parseInt(item.customPrice) || 0);
          const priceUsd = Math.round(priceIqd / EXCHANGE_RATE * 100) / 100;
          return { priceIqd, priceUsd, svc, hasSeconds, pps };
        });
        const createSubTotalIqd = itemPrices.reduce((s, p) => s + p.priceIqd, 0);
        let createGrandTotalIqd = createSubTotalIqd;
        const dValCreate = parseFloat(createForm.discount_value) || 0;
        if (createForm.discount_type === 'amount') createGrandTotalIqd -= dValCreate;
        if (createForm.discount_type === 'percentage') createGrandTotalIqd -= (createSubTotalIqd * (dValCreate / 100));
        createGrandTotalIqd = Math.max(0, createGrandTotalIqd);
        const createSubTotalUsd = itemPrices.reduce((s, p) => s + p.priceUsd, 0);
        const createGrandTotalUsd = Math.round((createGrandTotalIqd / EXCHANGE_RATE) * 100) / 100;

        const updateCreateItem = (idx, patch) => {
          setCreateItems(itms => itms.map((it, i) => i === idx ? { ...it, ...patch } : it));
        };
        const removeCreateItem = (idx) => {
          setCreateItems(itms => itms.filter((_, i) => i !== idx));
        };
        const addCreateItem = () => {
          setCreateItems(itms => [...itms, BLANK_QUICK_ITEM()]);
        };

        const handleCreateWithItems = async e => {
          e.preventDefault();
          if (!createForm.client_id) return toast.error('اختر العميل');
          try {
            const data = { ...createForm };
            if (createItems.length > 0) {
              data.lines = createItems.map((item, i) => {
                const p = itemPrices[i];
                const line_pps = p.hasSeconds ? p.pps : p.priceIqd;
                return {
                  description: item.description || p.svc.l,
                  service_type: item.service,
                  quantity: p.hasSeconds ? (parseInt(item.seconds) || 1) : 1,
                  unit: p.hasSeconds ? 'second' : 'unit',
                  unit_price_iqd: line_pps,
                  unit_price_usd: line_pps / EXCHANGE_RATE,
                };
              });
              data.subtotal_iqd = createSubTotalIqd;
              data.subtotal_usd = createSubTotalUsd;
              data.total_iqd = createGrandTotalIqd;
              data.total_usd = createGrandTotalUsd;
            } else {
              if (data.currency === 'IQD') data.total_usd = (parseFloat(data.total_iqd || 0) / EXCHANGE_RATE).toFixed(2);
              else data.total_iqd = Math.round(parseFloat(data.total_usd || 0) * EXCHANGE_RATE);
            }
            await createInvoice(data);
            toast.success('تم إنشاء الفاتورة');
            setShowCreate(false);
            setCreateForm(BLANK_CREATE);
            setCreateItems([]);
            load();
          } catch (err) { toast.error(err.response?.data?.error || 'خطأ في إنشاء الفاتورة'); }
        };

        return (
          <Modal onClose={() => setShowCreate(false)} title="إنشاء فاتورة جديدة" width={520}>
            <form onSubmit={handleCreateWithItems}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <LabeledInput label="العميل" required>
                  <SearchableSelect
                    value={createForm.client_id}
                    onChange={val => setCreateForm(f => ({ ...f, client_id: val }))}
                    options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                    placeholder="— اختر عميلاً —"
                    required
                    style={S.input}
                  />
                </LabeledInput>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="نوع الفاتورة">
                    <select value={createForm.invoice_type}
                      onChange={e => setCreateForm(f => ({ ...f, invoice_type: e.target.value }))}
                      style={S.input}>
                      <option value="project">مشروع</option>
                      <option value="subscription">اشتراك</option>
                      <option value="overage">تجاوز</option>
                    </select>
                  </LabeledInput>
                  <LabeledInput label="تاريخ الإصدار">
                    <input type="date" value={createForm.issue_date}
                      onChange={e => setCreateForm(f => ({ ...f, issue_date: e.target.value }))}
                      style={S.input} />
                  </LabeledInput>
                </div>

                {/* ── Basics of Calculation ── */}
                <div style={{ padding: '12px 0', borderTop: '2px dashed var(--border-light, #F1F5F9)' }}>
                  <div className="dark:!text-gray-100" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>أساسيات الحساب (بنود الفاتورة)</span>
                    {createItems.length === 0 && (
                      <button type="button" onClick={addCreateItem} style={{ ...btn('#17A589'), padding: '4px 10px', fontSize: 11 }}>
                        <Plus size={13} /> تفعيل الحساب التلقائي
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {createItems.map((item, idx) => {
                      const p = itemPrices[idx];
                      return (
                        <div key={idx} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', padding: 12, borderRadius: 10, position: 'relative' }}>
                          <button type="button" onClick={() => removeCreateItem(idx)}
                            style={{ position: 'absolute', left: 8, top: 8, background: 'none', border: 'none', color: '#E74C3C', cursor: 'pointer' }}>
                            <X size={14} />
                          </button>

                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, paddingLeft: 24 }}>
                            {QUICK_SERVICES.map(s => (
                              <button key={s.v} type="button"
                                onClick={() => updateCreateItem(idx, { service: s.v, seconds: s.hasSeconds ? 30 : '', customPrice: '' })}
                                style={{
                                  padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
                                  border: `2px solid ${item.service === s.v ? '#17A589' : 'var(--border-light, #E2E8F0)'}`,
                                  background: item.service === s.v ? '#E6F4F1' : 'var(--bg-flat-white, white)',
                                  color: item.service === s.v ? '#065F46' : 'var(--text-gray, #64748B)',
                                  fontWeight: 700, fontSize: 12, fontFamily: 'Tajawal, sans-serif'
                                }}>{s.l}</button>
                            ))}
                          </div>

                          <input value={item.description}
                            onChange={e => updateCreateItem(idx, { description: e.target.value })}
                            style={{ ...S.input, marginBottom: 8, fontSize: 12 }}
                            placeholder={`وصف الخدمة (مثال: فيديو ترويجي)...`} />

                          {/* طريقة احتساب السعر للبند */}
                          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                            <button type="button"
                              onClick={() => updateCreateItem(idx, { calculationMode: 'seconds' })}
                              style={{
                                flex: 1, padding: '5px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                                border: `2px solid ${(!item.calculationMode || item.calculationMode === 'seconds') ? '#8B5CF6' : 'var(--border-light, #E2E8F0)'}`,
                                background: (!item.calculationMode || item.calculationMode === 'seconds') ? '#F5F3FF' : 'var(--bg-flat-white, white)',
                                color: (!item.calculationMode || item.calculationMode === 'seconds') ? '#6D28D9' : 'var(--text-gray, #64748B)',
                                fontWeight: 700, fontSize: 11, outline: 'none'
                              }}>
                              ⏱️ احتساب بالثواني
                            </button>
                            <button type="button"
                              onClick={() => updateCreateItem(idx, { calculationMode: 'flat' })}
                              style={{
                                flex: 1, padding: '5px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                                border: `2px solid ${item.calculationMode === 'flat' ? '#10B981' : 'var(--border-light, #E2E8F0)'}`,
                                background: item.calculationMode === 'flat' ? '#ECFDF5' : 'var(--bg-flat-white, white)',
                                color: item.calculationMode === 'flat' ? '#047857' : 'var(--text-gray, #64748B)',
                                fontWeight: 700, fontSize: 11, outline: 'none'
                              }}>
                              💵 سعر ثابت مخصص
                            </button>
                          </div>

                          {p.hasSeconds ? (
                            <div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                                {[15, 30, 45, 60].map(s => (
                                  <button key={s} type="button" onClick={() => updateCreateItem(idx, { seconds: s })}
                                    style={{
                                      padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                                      border: `1.5px solid ${item.seconds == s ? '#17A589' : 'var(--border-light, #E2E8F0)'}`,
                                      background: item.seconds == s ? '#E6F4F1' : 'var(--bg-flat-white, white)', fontSize: 11
                                    }}>{s}ث</button>
                                ))}
                                <input type="text" inputMode="decimal" value={item.seconds}
                                  onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                                  onChange={e => updateCreateItem(idx, { seconds: parseInt(e.target.value) || '' })}
                                  style={{ ...S.input, width: 70, padding: '4px 6px', fontSize: 11 }} placeholder="ثواني..." />
                              </div>
                              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-dark-s, #374151)', background: 'var(--bg-flat-white, #FFFFFF)', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', marginTop: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                                  <Clock size={14} color="#17A589" />
                                  <span style={{ fontWeight: 600 }}>سعر الثانية:</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input type="text" inputMode="decimal" value={item.pricePerSecond}
                                    onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                                    onChange={e => updateCreateItem(idx, { pricePerSecond: parseInt(e.target.value) || '' })}
                                    style={{ ...S.input, width: 85, padding: '6px 10px', fontSize: 13, textAlign: 'center', borderColor: '#17A589' }} />
                                  <span className="dark:!text-gray-400" style={{ fontWeight: 700, color: 'var(--text-gray, #64748B)', fontSize: 12 }}>د.ع / ث</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <input type="text" inputMode="decimal" value={item.customPrice}
                              onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                              onChange={e => updateCreateItem(idx, { customPrice: e.target.value })}
                              style={{ ...S.input, fontSize: 12 }} placeholder="السعر الكلي بالدينار..." />
                          )}

                          {p.priceIqd > 0 && (
                            <div style={{ textAlign: 'left', marginTop: 6, fontSize: 12, fontWeight: 700, color: '#17A589' }}>
                              {formatIQD(p.priceIqd)} <span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontWeight: 400 }}>≈ {formatUSD(p.priceUsd)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {createItems.length > 0 && (
                      <button type="button" onClick={addCreateItem}
                        className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ padding: '8px', border: '2px dashed var(--border-light, #E2E8F0)', borderRadius: 10, color: 'var(--text-gray, #64748B)', fontSize: 13, cursor: 'pointer', background: 'var(--bg-flat-white, white)' }}>
                        + إضافة خدمة إضافية لهذه الفاتورة
                      </button>
                    )}
                  </div>
                </div>

                {createItems.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <LabeledInput label="الخصم">
                      <select value={createForm.discount_type} onChange={e => setCreateForm(f => ({ ...f, discount_type: e.target.value }))} style={S.input}>
                        <option value="none">بدون خصم</option>
                        <option value="amount">مبلغ ثابت</option>
                        <option value="percentage">نسبة مئوية (%)</option>
                      </select>
                    </LabeledInput>
                    {createForm.discount_type !== 'none' && (
                      <LabeledInput label={createForm.discount_type === 'percentage' ? 'نسبة الخصم %' : 'مبلغ الخصم'}>
                        <input type="text" inputMode="decimal" value={createForm.discount_value} onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setCreateForm(f => ({ ...f, discount_value: e.target.value }))} style={S.input} />
                      </LabeledInput>
                    )}
                  </div>
                )}
                {createItems.length === 0 ? (
                  <LabeledInput
                    label={createForm.currency === 'IQD' ? 'المبلغ الكلي بالدينار' : 'المبلغ الكلي بالدولار'} required
                    hint={createPreviewUSD > 0 ? (createForm.currency === 'IQD' ? `≈ ${formatUSD(createPreviewUSD)}` : `≈ ${formatIQD(parseFloat(createForm.total_usd || 0) * EXCHANGE_RATE)}`) : undefined}>
                    {createForm.currency === 'IQD'
                      ? <input type="text" inputMode="decimal" value={createForm.total_iqd} onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setCreateForm(f => ({ ...f, total_iqd: e.target.value }))} style={S.input} required />
                      : <input type="text" inputMode="decimal" value={createForm.total_usd} onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setCreateForm(f => ({ ...f, total_usd: e.target.value }))} style={S.input} required />
                    }
                  </LabeledInput>
                ) : (
                  <>
                    {createForm.discount_type !== 'none' && (
                      <div style={{ fontSize: 13, color: '#E74C3C', marginBottom: 6, fontWeight: 700 }}>
                        يوجد خصم: {createForm.discount_type === 'percentage' ? '%' + createForm.discount_value : formatIQD(createForm.discount_value)}
                      </div>
                    )}
                    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                      <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 2 }}>إجمالي الحساب التلقائي</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#17A589' }}>{formatIQD(createGrandTotalIqd)}</div>
                      <div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-muted, #94A3B8)' }}>≈ {formatUSD(createGrandTotalUsd)}</div>
                    </div>
                  </>)}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="بواسطة (إحالة موظف)">
                    <select value={createForm.referring_employee_id}
                      onChange={e => handleEmployeeSelect(e.target.value, setCreateForm)}
                      style={S.input}>
                      <option value="">— لا يوجد —</option>
                      {employees.filter(e => e.active || e.id === Number(createForm.referring_employee_id)).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </LabeledInput>
                  <LabeledInput label="نسبة العمولة (%)">
                    <input type="text" inputMode="decimal" value={createForm.commission_rate}
                      onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                      onChange={e => setCreateForm(f => ({ ...f, commission_rate: e.target.value }))}
                      style={S.input} placeholder="مثال: 10" />
                    {parseFloat(createForm.commission_rate || 0) > 0 && createGrandTotalIqd > 0 && (
                      <div style={{ fontSize: 11, color: '#17A589', marginTop: 4, fontWeight: 700 }}>قيمة العمولة المحسوبة: {formatIQD(createGrandTotalIqd * (parseFloat(createForm.commission_rate) / 100))}</div>
                    )}
                  </LabeledInput>
                </div>

                <LabeledInput label="ملاحظات">
                  <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2} style={{ ...S.input, resize: 'vertical' }} placeholder="ملاحظات اختيارية..." />
                </LabeledInput>

                <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                  <button type="submit" style={btn('#17A589')}>إنشاء الفاتورة</button>
                  <button type="button" onClick={() => setShowCreate(false)} style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}>إلغاء</button>
                </div>
              </div>
            </form>
          </Modal>
        );
      })()}

      {/* ═══════════════ PAYMENT MODAL ═══════════════ */}
      {payModal && (
        <Modal onClose={() => setPayModal(null)} title={`تسجيل دفعة — ${payModal.invoice_number}`}>
          {/* Invoice summary strip */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 18 }}>
            {[
              { l: 'الإجمالي', v: payModal.total_iqd, u: payModal.total_usd, c: 'var(--text-dark-h, #0F1E33)' },
              { l: 'مدفوع', v: payModal.paid_iqd, u: payModal.paid_usd, c: '#17A589' },
              { l: 'المتبقي', v: (payModal.total_iqd || 0) - (payModal.paid_iqd || 0), c: '#E74C3C' },
            ].map(x => (
              <div key={x.l} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginBottom: 3 }}>{x.l}</div>
                <div style={{ fontWeight: 700, color: x.c, fontSize: 14 }}>{formatIQD(x.v)}</div>
                <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{formatUSD(x.u ?? iqd2usd(x.v))}</div>
              </div>
            ))}
          </div>

          <form onSubmit={handlePay}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              <LabeledInput label="المبلغ بالدينار" required
                hint={payPreviewUSD > 0 ? `≈ ${formatUSD(payPreviewUSD)}` : undefined}>
                <input type="text" inputMode="decimal" value={payForm.amount_iqd}
                  onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                  onChange={e => setPayForm(f => ({ ...f, amount_iqd: e.target.value }))}
                  style={S.input} required autoFocus />
              </LabeledInput>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <LabeledInput label="طريقة الدفع">
                  <select value={payForm.method}
                    onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}
                    style={S.input}>
                    {PAY_METHODS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </LabeledInput>
                <LabeledInput label="تاريخ الدفع">
                  <input type="date" value={payForm.pay_date}
                    onChange={e => setPayForm(f => ({ ...f, pay_date: e.target.value }))}
                    style={S.input} />
                </LabeledInput>
              </div>

              <LabeledInput label="رقم الوصل">
                <input value={payForm.receipt_ref}
                  onChange={e => setPayForm(f => ({ ...f, receipt_ref: e.target.value }))}
                  style={S.input} placeholder="اختياري..." />
              </LabeledInput>

              <LabeledInput label="ملاحظة">
                <input value={payForm.note}
                  onChange={e => setPayForm(f => ({ ...f, note: e.target.value }))}
                  style={S.input} placeholder="اختياري..." />
              </LabeledInput>

              <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                <button type="submit" style={btn('#17A589')}><CheckCircle size={14} /> تأكيد الدفعة</button>
                <button type="button" onClick={() => setPayModal(null)} style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}>إلغاء</button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* ═══════════════ DETAIL MODAL ═══════════════ */}
      {detailModal && (() => {
        const st = INV_STATUS[detailModal.payment_status] || INV_STATUS.unpaid;
        const remain = (detailModal.total_iqd || 0) - (detailModal.paid_iqd || 0);
        const over = isOverdue(detailModal);
        const isCancelled = detailModal.payment_status === 'cancelled' || detailModal.is_cancelled;
        const isPaid = detailModal.payment_status === 'paid';

        const handleCancel = async () => {
          const reason = window.prompt('سبب الإلغاء (اختياري):');
          if (reason === null) return; // user pressed Cancel on prompt
          try {
            await cancelInvoice(detailModal.id, reason);
            toast.success('تم إلغاء الفاتورة');
            setDetailModal(null);
            load();
          } catch (err) { toast.error(err.response?.data?.error || 'خطأ في إلغاء الفاتورة'); }
        };

        const handleReversePayment = async (paymentId) => {
          const reason = window.prompt('سبب عكس الدفعة:');
          if (!reason?.trim()) return toast.error('يجب إدخال سبب العكس');
          try {
            await reversePayment(detailModal.id, paymentId, reason);
            toast.success('تم عكس الدفعة');
            // Refresh detail modal with updated data
            const updated = await getInvoice(detailModal.id);
            setDetailModal(updated);
            load();
          } catch (err) { toast.error(err.response?.data?.error || 'خطأ في عكس الدفعة'); }
        };

        return (
          <Modal onClose={() => setDetailModal(null)} title={`تفاصيل ${detailModal.invoice_number}`} width={500}>
            <div style={{ marginBottom: 12 }}>
              <span style={pill(st.bg, st.color)}>{st.label}</span>
              {over && !isCancelled && <span style={{ ...pill('#FEF2F2', '#E74C3C'), marginRight: 6 }}>متأخر</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <DetailRow label="العميل"><strong>{detailModal.client_name}</strong></DetailRow>
              <DetailRow label="نوع الفاتورة">{INV_TYPE[detailModal.invoice_type] || detailModal.invoice_type}</DetailRow>
              <DetailRow label="تاريخ الإصدار">{detailModal.issue_date}</DetailRow>
              {detailModal.referring_employee_id && (
                <DetailRow label="بواسطة (موظف)">
                  <span style={{ fontWeight: 600, color: '#4F46E5' }}>
                    {employees.find(e => e.id === detailModal.referring_employee_id)?.name || detailModal.referring_employee_id}
                  </span>
                  {' '}
                  <span className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>
                    (عمولة {detailModal.commission_rate}%)
                  </span>
                </DetailRow>
              )}
              <DetailRow label="الإجمالي">
                <Dual iqd={detailModal.total_iqd} usd={detailModal.total_usd} bold />
              </DetailRow>
              <DetailRow label="المدفوع">
                <Dual iqd={detailModal.paid_iqd} usd={detailModal.paid_usd} />
              </DetailRow>
              <DetailRow label="المتبقي">
                <Dual iqd={remain} redIqd={remain > 0} bold={remain > 0} />
              </DetailRow>
              {detailModal.notes && (
                <DetailRow label="ملاحظات">
                  <span className="dark:!text-gray-400" style={{ color: 'var(--text-gray, #64748B)', maxWidth: 220, textAlign: 'left' }}>{detailModal.notes}</span>
                </DetailRow>
              )}
            </div>

            {/* ── Payments list ── */}
            {detailModal.payments?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="dark:!text-gray-400" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-gray, #64748B)', marginBottom: 6 }}>الدفعات المسجلة</div>
                {detailModal.payments.map(p => (
                  <div key={p.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '7px 10px', borderRadius: 8, marginBottom: 4,
                    background: p.is_reversed ? '#F8F9FA' : '#F0FDF4',
                    opacity: p.is_reversed ? 0.6 : 1
                  }}>
                    <div>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: p.is_reversed ? 'var(--text-muted, #94A3B8)' : '#17A589',
                        textDecoration: p.is_reversed ? 'line-through' : 'none'
                      }}>
                        {formatIQD(p.amount_iqd)}
                      </div>
                      <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{p.payment_date || p.pay_date || ''} · {p.method || ''}</div>
                    </div>
                    {!p.is_reversed && (
                      <button onClick={() => handleReversePayment(p.id)}
                        style={{ ...btn('#FEF2F2', '#E74C3C'), padding: '4px 10px', fontSize: 11 }}>
                        عكس
                      </button>
                    )}
                    {p.is_reversed && <span className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>معكوس</span>}
                  </div>
                ))}
              </div>
            )}

            {/* ── Actions ── */}
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!isPaid && !isCancelled && (
                <button onClick={() => { setDetailModal(null); openPayModal(detailModal); }}
                  style={{ ...btn('#17A589'), width: '100%', justifyContent: 'center' }}>
                  <CreditCard size={14} /> تسجيل دفعة
                </button>
              )}
              {(!isPaid || isOwnerOrAdmin) && !isCancelled && (
                <button onClick={() => {
                  setDetailModal(null);
                  openEditModal(detailModal);
                }}
                  style={{ ...btn('#EEF2FF', '#4F46E5'), width: '100%', justifyContent: 'center', border: '1px solid #C7D2FE' }}>
                  <Edit3 size={14} /> تعديل الفاتورة
                </button>
              )}
              {!isCancelled && (
                <button onClick={handleCancel}
                  style={{ ...btn('#F8F9FA', '#E74C3C'), width: '100%', justifyContent: 'center', border: '1px solid #FECACA' }}>
                  <X size={14} /> إلغاء الفاتورة
                </button>
              )}
              {isOwnerOrAdmin && (
                <button onClick={async () => {
                  if (!window.confirm('حذف الفاتورة نهائياً من قاعدة البيانات؟ لا يمكن التراجع')) return;
                  try {
                    await deleteInvoice(detailModal.id);
                    toast.success('تم حذف الفاتورة نهائياً');
                    setDetailModal(null);
                    load();
                  } catch (err) { toast.error(err.response?.data?.error || 'خطأ في الحذف'); }
                }}
                  style={{ ...btn('#FEE2E2', '#991B1B'), width: '100%', justifyContent: 'center' }}>
                  🗑️ حذف نهائي
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ═══════════════ EDIT MODAL (with line items) ═══════════════ */}
      {editModal && (() => {
        // Compute per-item prices for edit items
        const editItems = editForm.items || [];
        const editItemPrices = editItems.map(item => {
          const svc = QUICK_SERVICES.find(s => s.v === item.service) || QUICK_SERVICES.find(s => s.v === 'custom');
          // Respect calculationMode if explicitly selected, else fallback to backward-compatible smart detection
          const hasSeconds = item.calculationMode
            ? (item.calculationMode === 'seconds')
            : ((item.customPrice && !item.seconds) ? false : (svc?.hasSeconds ?? false));
          const pps = parseInt(item.pricePerSecond) || 1500;
          let priceIqd, priceUsd;
          if (hasSeconds) {
            priceIqd = (parseInt(item.seconds) || 0) * pps;
            priceUsd = Math.round(priceIqd / EXCHANGE_RATE * 100) / 100;
          } else {
            priceIqd = parseFloat(item.customPrice) || 0;
            priceUsd = Math.round(priceIqd / EXCHANGE_RATE * 100) / 100;
          }
          return { priceIqd, priceUsd, svc, hasSeconds, pps };
        });
        const editSubTotalIqd = editItemPrices.reduce((s, p) => s + p.priceIqd, 0);
        let editGrandTotalIqd = editSubTotalIqd;
        const dValEdit = parseFloat(editForm.discount_value) || 0;
        if (editForm.discount_type === 'amount') editGrandTotalIqd -= dValEdit;
        if (editForm.discount_type === 'percentage') editGrandTotalIqd -= (editSubTotalIqd * (dValEdit / 100));
        editGrandTotalIqd = Math.max(0, editGrandTotalIqd);
        const editSubTotalUsd = editItemPrices.reduce((s, p) => s + p.priceUsd, 0);
        const editGrandTotalUsd = Math.round((editGrandTotalIqd / EXCHANGE_RATE) * 100) / 100;

        const updateEditItem = (idx, patch) => {
          setEditForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
        };
        const removeEditItem = (idx) => {
          setEditForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
        };
        const addEditItem = () => {
          setEditForm(f => ({ ...f, items: [...f.items, BLANK_QUICK_ITEM()] }));
        };

        const handleEditSubmit = async (e) => {
          e.preventDefault();
          if (editItems.length === 0) return toast.error('أضف خدمة واحدة على الأقل');
          if (editGrandTotalIqd <= 0) return toast.error('أدخل الأسعار / المدد');

          // Build lines for backend
          const lines = editItems.map((item, i) => {
            const p = editItemPrices[i];
            const p_iqd = p.hasSeconds ? p.pps : p.priceIqd;
            return {
              description: item.description || p.svc?.l || '',
              service_type: item.service,
              quantity: p.hasSeconds ? (parseInt(item.seconds) || 1) : 1,
              unit: p.hasSeconds ? 'second' : 'unit',
              unit_price_iqd: p_iqd,
              unit_price_usd: Math.round(p_iqd / EXCHANGE_RATE * 100) / 100,
            };
          });

          // Build notes summary
          const itemsSummary = editItems.map((item, i) => {
            const p = editItemPrices[i];
            const label = (p.svc?.l || '').replace(/^[^\s]+\s/, '');
            return p.hasSeconds ? `${label} ${item.seconds}ث` : `${label} ${formatIQD(p.priceIqd || 0)}`;
          }).join(' + ');
          const noteText = editForm.notes ? editForm.notes : itemsSummary;

          try {
            await updateInvoice(editModal.id, {
              client_id: editForm.client_id,
              invoice_type: editForm.invoice_type,
              issue_date: editForm.issue_date,
              due_date: editForm.due_date,
              notes: noteText,
              subtotal_iqd: editSubTotalIqd,
              subtotal_usd: editSubTotalUsd,
              discount_type: editForm.discount_type,
              discount_value: editForm.discount_value,
              total_iqd: editGrandTotalIqd,
              total_usd: editGrandTotalUsd,
              lines,
              referring_employee_id: editForm.referring_employee_id,
              commission_rate: editForm.commission_rate
            });
            toast.success('✅ تم تحديث الفاتورة');
            setEditModal(null);
            load();
          } catch (err) {
            toast.error(err.response?.data?.error || 'خطأ في تحديث الفاتورة');
          }
        };

        return (
          <Modal onClose={() => setEditModal(null)} title={`✏️ تعديل ${editModal.invoice_number}`} width={560}>
            <form onSubmit={handleEditSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* ── Client ── */}
                <LabeledInput label="العميل" required>
                  <SearchableSelect
                    value={editForm.client_id}
                    onChange={val => setEditForm(f => ({ ...f, client_id: val }))}
                    options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                    placeholder="— اختر عميلاً —"
                    required
                    style={S.input}
                  />
                </LabeledInput>

                {/* ── Dates + Type ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <LabeledInput label="نوع الفاتورة">
                    <select value={editForm.invoice_type}
                      onChange={e => setEditForm(f => ({ ...f, invoice_type: e.target.value }))}
                      style={S.input}>
                      <option value="project">مشروع</option>
                      <option value="subscription">اشتراك</option>
                      <option value="overage">تجاوز</option>
                    </select>
                  </LabeledInput>
                  <LabeledInput label="تاريخ الإصدار">
                    <input type="date" value={editForm.issue_date}
                      onChange={e => setEditForm(f => ({ ...f, issue_date: e.target.value }))}
                      style={S.input} />
                  </LabeledInput>
                  <LabeledInput label="تاريخ الاستحقاق">
                    <input type="date" value={editForm.due_date}
                      onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                      style={S.input} />
                  </LabeledInput>
                </div>

                {/* ── LINE ITEMS ── */}
                <div className="dark:!text-gray-400" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-gray, #64748B)', marginBottom: -6 }}>الخدمات / البنود</div>
                {editItems.map((item, idx) => {
                  const p = editItemPrices[idx];
                  const hasSeconds = p.hasSeconds;
                  return (
                    <div key={idx} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{
                      background: '#FAFBFC', border: '1px solid var(--border-light, #E2E8F0)', borderRadius: 12,
                      padding: '12px 14px', position: 'relative'
                    }}>
                      {/* Remove item button */}
                      {editItems.length > 1 && (
                        <button type="button" onClick={() => removeEditItem(idx)}
                          style={{
                            position: 'absolute', top: 8, left: 8, background: '#FEE2E2', border: 'none',
                            borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#DC2626'
                          }}>×</button>
                      )}
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9B8EC4', marginBottom: 8 }}>بند {idx + 1}</div>

                      {/* Service type */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {QUICK_SERVICES.map(s => (
                          <button key={s.v} type="button"
                            onClick={() => updateEditItem(idx, { service: s.v })}
                            style={{
                              padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                              border: `2px solid ${item.service === s.v ? '#F59E0B' : 'var(--border-light, #E2E8F0)'}`,
                              background: item.service === s.v ? '#FEF3C7' : 'var(--bg-flat-white, white)',
                              color: item.service === s.v ? '#92400E' : 'var(--text-gray, #64748B)',
                              fontWeight: 700, fontSize: 12, fontFamily: 'Tajawal, sans-serif'
                            }}>{s.l}</button>
                        ))}
                      </div>

                      {/* Description (optional) */}
                      <input value={item.description}
                        onChange={e => updateEditItem(idx, { description: e.target.value })}
                        style={{ ...S.input, marginBottom: 8, fontSize: 12 }}
                        placeholder={`وصف اختياري (مثال: فيديو إعلان العطر)...`} />

                      {/* طريقة احتساب السعر للبند */}
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        <button type="button"
                          onClick={() => updateEditItem(idx, { calculationMode: 'seconds' })}
                          style={{
                            flex: 1, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                            border: `2px solid ${(!item.calculationMode || item.calculationMode === 'seconds') ? '#8B5CF6' : 'var(--border-light, #E2E8F0)'}`,
                            background: (!item.calculationMode || item.calculationMode === 'seconds') ? '#F5F3FF' : 'var(--bg-flat-white, white)',
                            color: (!item.calculationMode || item.calculationMode === 'seconds') ? '#6D28D9' : 'var(--text-gray, #64748B)',
                            fontWeight: 700, fontSize: 11, outline: 'none'
                          }}>
                          ⏱️ احتساب بالثواني
                        </button>
                        <button type="button"
                          onClick={() => updateEditItem(idx, { calculationMode: 'flat' })}
                          style={{
                            flex: 1, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
                            border: `2px solid ${item.calculationMode === 'flat' ? '#10B981' : 'var(--border-light, #E2E8F0)'}`,
                            background: item.calculationMode === 'flat' ? '#ECFDF5' : 'var(--bg-flat-white, white)',
                            color: item.calculationMode === 'flat' ? '#047857' : 'var(--text-gray, #64748B)',
                            fontWeight: 700, fontSize: 11, outline: 'none'
                          }}>
                          💵 سعر ثابت مخصص
                        </button>
                      </div>

                      {/* Seconds or price */}
                      {hasSeconds ? (
                        <div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                            {[15, 30, 45, 60, 90, 120].map(s => (
                              <button key={s} type="button"
                                onClick={() => updateEditItem(idx, { seconds: s })}
                                style={{
                                  padding: '5px 11px', borderRadius: 7, cursor: 'pointer',
                                  border: `2px solid ${item.seconds == s ? '#F59E0B' : 'var(--border-light, #E2E8F0)'}`,
                                  background: item.seconds == s ? '#FEF3C7' : 'var(--bg-flat-white, white)',
                                  color: item.seconds == s ? '#92400E' : 'var(--text-gray, #64748B)',
                                  fontWeight: 700, fontSize: 12, fontFamily: 'Tajawal, sans-serif'
                                }}>{s}ث</button>
                            ))}
                          </div>
                          <input type="text" inputMode="decimal" value={item.seconds}
                            onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                            onChange={e => updateEditItem(idx, { seconds: parseInt(e.target.value) || '' })}
                            style={{ ...S.input, fontSize: 12, marginBottom: 8 }} placeholder="ثواني مخصصة..." />
                          <div className="dark:!text-gray-400" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-gray, #64748B)' }}>
                            <span>سعر الثانية:</span>
                            <input type="text" inputMode="decimal" value={item.pricePerSecond}
                              onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                              onChange={e => updateEditItem(idx, { pricePerSecond: parseInt(e.target.value) || '' })}
                              style={{ ...S.input, fontSize: 12, width: 100, padding: '4px 8px' }} placeholder="1500" />
                            <span>د.ع</span>
                          </div>
                        </div>
                      ) : (
                        <input type="text" inputMode="decimal" value={item.customPrice}
                          onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                          onChange={e => updateEditItem(idx, { customPrice: e.target.value })}
                          style={{ ...S.input, fontSize: 12 }}
                          placeholder="السعر بالدينار (مثال: 150000)" />
                      )}

                      {/* Item price */}
                      {p.priceIqd > 0 && (
                        <div style={{ textAlign: 'left', marginTop: 6, fontSize: 12, fontWeight: 700, color: '#17A589' }}>
                          {formatIQD(p.priceIqd)} <span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontWeight: 400 }}>≈ {formatUSD(p.priceUsd)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add item button */}
                <button type="button" onClick={addEditItem}
                  className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
                    padding: '8px 16px', borderRadius: 10, cursor: 'pointer', border: '2px dashed #CBD5E1',
                    background: 'var(--bg-flat-white, white)', color: 'var(--text-gray, #64748B)', fontWeight: 700, fontSize: 13,
                    fontFamily: 'Tajawal, sans-serif', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6
                  }}>
                  <Plus size={15} /> إضافة خدمة أخرى
                </button>

                {/* ── Grand total preview ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="الخصم">
                    <select value={editForm.discount_type} onChange={e => setEditForm(f => ({ ...f, discount_type: e.target.value }))} style={S.input}>
                      <option value="none">بدون خصم</option>
                      <option value="amount">مبلغ ثابت</option>
                      <option value="percentage">نسبة مئوية (%)</option>
                    </select>
                  </LabeledInput>
                  {editForm.discount_type !== 'none' && (
                    <LabeledInput label={editForm.discount_type === 'percentage' ? 'نسبة الخصم %' : 'مبلغ الخصم'}>
                      <input type="text" inputMode="decimal" value={editForm.discount_value} onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setEditForm(f => ({ ...f, discount_value: e.target.value }))} style={S.input} />
                    </LabeledInput>
                  )}
                </div>

                {editGrandTotalIqd > 0 && (
                  <div style={{
                    background: '#F0FDF4', border: '1px solid #BBF7D0',
                    borderRadius: 10, padding: '14px 16px', textAlign: 'center'
                  }}>
                    {editForm.discount_type !== 'none' && (
                      <div style={{ fontSize: 13, color: '#E74C3C', marginBottom: 6, fontWeight: 700 }}>
                        المبلغ قبل الخصم: {formatIQD(editSubTotalIqd)} (خصم: {editForm.discount_type === 'percentage' ? '%' + editForm.discount_value : formatIQD(editForm.discount_value)})
                      </div>
                    )}
                    <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4 }}>
                      الإجمالي ({editItems.length} {editItems.length === 1 ? 'خدمة' : 'خدمات'})
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#17A589' }}>
                      {formatIQD(editGrandTotalIqd)}
                    </div>
                    <div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-muted, #94A3B8)', marginTop: 2 }}>
                      ≈ {formatUSD(editGrandTotalUsd)}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="بواسطة (إحالة موظف)">
                    <select value={editForm.referring_employee_id}
                      onChange={e => handleEmployeeSelect(e.target.value, setEditForm)}
                      style={S.input}>
                      <option value="">— لا يوجد —</option>
                      {employees.filter(e => e.active || e.id === Number(editForm.referring_employee_id)).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </LabeledInput>
                  <LabeledInput label="نسبة العمولة (%)">
                    <input type="text" inputMode="decimal" value={editForm.commission_rate}
                      onInput={e => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                      onChange={e => setEditForm(f => ({ ...f, commission_rate: e.target.value }))}
                      style={S.input} placeholder="مثال: 10" />
                  </LabeledInput>
                </div>

                <LabeledInput label="ملاحظات">
                  <textarea value={editForm.notes}
                    onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2} style={{ ...S.input, resize: 'vertical' }} placeholder="اختياري..." />
                </LabeledInput>

                <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                  <button type="submit" style={btn('#4F46E5', '#fff')}><Edit3 size={14} /> حفظ التعديلات ({editItems.length} بند)</button>
                  <button type="button" onClick={() => setEditModal(null)} style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}>إلغاء</button>
                </div>
              </div>
            </form>
          </Modal>
        );
      })()}
    </div>
  );
}
